import _slicedToArray from "C:\\Users\\Kristaps\\projects\\proj261\\site\\node_modules\\@babel\\runtime\\helpers\\esm\\slicedToArray.js";
var _excluded = ["id", "value", "onChange", "onSelect", "onToggle", "onKeyDown", "onKeyPress", "onCurrentDateChange", "inputProps", "calendarProps", "timeInputProps", "autoFocus", "tabIndex", "disabled", "readOnly", "className", "valueFormat", "valueDisplayFormat", "valueEditFormat", "containerClassName", "name", "selectIcon", "placeholder", "includeTime", "min", "max", "open", "dropUp", "parse", "messages", "formats", "currentDate", "popupTransition", "popupComponent", "timePrecision", "aria-labelledby", "aria-describedby"];

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

import cn from 'classnames';
import React, { useImperativeHandle, useRef, useCallback } from 'react';
import { useUncontrolled } from 'uncontrollable';
import Calendar from './Calendar';
import DatePickerInput from './DatePickerInput';
import { calendar } from './Icon';
import { useLocalizer } from './Localization';
import BasePopup from './Popup';
import TimeInput from './TimeInput';
import Widget from './Widget';
import WidgetPicker from './WidgetPicker';
import dates from './dates';
import useDropdownToggle from './useDropdownToggle';
import useTabTrap from './useTabTrap';
import useFocusManager from './useFocusManager';
import { notify, useFirstFocusedRender, useInstanceId } from './WidgetHelpers';
import useEventCallback from '@restart/hooks/useEventCallback';
import InputAddon from './InputAddon';
var defaultProps = Object.assign({}, Calendar.defaultProps, {
  min: new Date(1900, 0, 1),
  max: new Date(2099, 11, 31),
  selectIcon: calendar,
  formats: {}
});
/**
 * ---
 * subtitle: DatePicker, TimePicker
 * localized: true
 * shortcuts:
 *   - { key: alt + down arrow, label:  open calendar or time }
 *   - { key: alt + up arrow, label: close calendar or time }
 *   - { key: down arrow, label: move focus to next item }
 *   - { key: up arrow, label: move focus to previous item }
 *   - { key: home, label: move focus to first item }
 *   - { key: end, label: move focus to last item }
 *   - { key: enter, label: select focused item }
 *   - { key: any key, label: search list for item starting with key }
 * ---
 *
 * @public
 * @extends Calendar
 */

var DatePicker = /*#__PURE__*/React.forwardRef(function (uncontrolledProps, outerRef) {
  var _useUncontrolled = useUncontrolled(uncontrolledProps, {
    open: 'onToggle',
    value: 'onChange',
    currentDate: 'onCurrentDateChange'
  }),
      id = _useUncontrolled.id,
      value = _useUncontrolled.value,
      onChange = _useUncontrolled.onChange,
      onSelect = _useUncontrolled.onSelect,
      onToggle = _useUncontrolled.onToggle,
      onKeyDown = _useUncontrolled.onKeyDown,
      onKeyPress = _useUncontrolled.onKeyPress,
      onCurrentDateChange = _useUncontrolled.onCurrentDateChange,
      inputProps = _useUncontrolled.inputProps,
      calendarProps = _useUncontrolled.calendarProps,
      timeInputProps = _useUncontrolled.timeInputProps,
      autoFocus = _useUncontrolled.autoFocus,
      tabIndex = _useUncontrolled.tabIndex,
      disabled = _useUncontrolled.disabled,
      readOnly = _useUncontrolled.readOnly,
      className = _useUncontrolled.className,
      valueFormat = _useUncontrolled.valueFormat,
      _useUncontrolled$valu = _useUncontrolled.valueDisplayFormat,
      valueDisplayFormat = _useUncontrolled$valu === void 0 ? valueFormat : _useUncontrolled$valu,
      _useUncontrolled$valu2 = _useUncontrolled.valueEditFormat,
      valueEditFormat = _useUncontrolled$valu2 === void 0 ? valueFormat : _useUncontrolled$valu2,
      containerClassName = _useUncontrolled.containerClassName,
      name = _useUncontrolled.name,
      selectIcon = _useUncontrolled.selectIcon,
      placeholder = _useUncontrolled.placeholder,
      _useUncontrolled$incl = _useUncontrolled.includeTime,
      includeTime = _useUncontrolled$incl === void 0 ? false : _useUncontrolled$incl,
      min = _useUncontrolled.min,
      max = _useUncontrolled.max,
      open = _useUncontrolled.open,
      dropUp = _useUncontrolled.dropUp,
      parse = _useUncontrolled.parse,
      messages = _useUncontrolled.messages,
      formats = _useUncontrolled.formats,
      currentDate = _useUncontrolled.currentDate,
      popupTransition = _useUncontrolled.popupTransition,
      _useUncontrolled$popu = _useUncontrolled.popupComponent,
      Popup = _useUncontrolled$popu === void 0 ? BasePopup : _useUncontrolled$popu,
      timePrecision = _useUncontrolled.timePrecision,
      ariaLabelledby = _useUncontrolled['aria-labelledby'],
      ariaDescribedby = _useUncontrolled['aria-describedby'],
      elementProps = _objectWithoutPropertiesLoose(_useUncontrolled, _excluded);

  var localizer = useLocalizer(messages, formats);
  var ref = useRef(null);
  var calRef = useRef(null);
  var tabTrap = useTabTrap(calRef);
  var inputId = useInstanceId(id, '_input');
  var dateId = useInstanceId(id, '_date');
  var currentFormat = includeTime ? 'datetime' : 'date';
  var toggle = useDropdownToggle(open, onToggle);

  var _useFocusManager = useFocusManager(ref, uncontrolledProps, {
    didHandle: function didHandle(focused) {
      if (!focused) {
        toggle.close();
        tabTrap.stop();
      } else if (open) {
        tabTrap.focus();
      }
    }
  }),
      _useFocusManager2 = _slicedToArray(_useFocusManager, 2),
      focusEvents = _useFocusManager2[0],
      focused = _useFocusManager2[1];

  var dateParser = useCallback(function (str) {
    var _localizer$parseDate, _ref;

    if (typeof parse == 'function') {
      var _parse;

      return (_parse = parse(str, localizer)) != null ? _parse : null;
    }

    return (_localizer$parseDate = localizer.parseDate(str, (_ref = parse != null ? parse : valueEditFormat) != null ? _ref : valueDisplayFormat)) != null ? _localizer$parseDate : null;
  }, [localizer, parse, valueDisplayFormat, valueEditFormat]);
  /**
   * Handlers
   */

  var handleChange = useEventCallback(function (date, str, constrain) {
    if (readOnly || disabled) return;
    if (constrain) date = inRangeValue(date);

    if (onChange) {
      if (date == null || value == null) {
        if (date != value //eslint-disable-line eqeqeq
        ) onChange(date, str);
      } else if (!dates.eq(date, value)) {
        onChange(date, str);
      }
    }
  });
  var handleKeyDown = useEventCallback(function (e) {
    if (readOnly) return;
    notify(onKeyDown, [e]);
    if (e.defaultPrevented) return;

    if (e.key === 'Escape' && open) {
      toggle.close();
    } else if (e.altKey) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        toggle.open();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        toggle.close();
      }
    }
  });
  var handleKeyPress = useEventCallback(function (e) {
    notify(onKeyPress, [e]);
    if (e.defaultPrevented) return;
  });
  var handleDateSelect = useEventCallback(function (date) {
    var _ref$current;

    var dateTime = dates.merge(date, value, currentDate);
    var dateStr = formatDate(date);
    if (!includeTime) toggle.close();
    notify(onSelect, [dateTime, dateStr]);
    handleChange(dateTime, dateStr, true);
    (_ref$current = ref.current) == null ? void 0 : _ref$current.focus();
  });
  var handleTimeChange = useEventCallback(function (date) {
    handleChange(date, formatDate(date), true);
  });
  var handleCalendarClick = useEventCallback(function (e) {
    if (readOnly || disabled) return; // prevents double clicks when in a <label>

    e.preventDefault();
    toggle();
  });

  var handleOpening = function handleOpening() {
    tabTrap.start();
    requestAnimationFrame(function () {
      tabTrap.focus();
    });
  };

  var handleClosing = function handleClosing() {
    tabTrap.stop();
    if (focused) focus();
  };
  /**
   * Methods
   */


  function focus() {
    var _calRef$current, _ref$current2;

    if (open) (_calRef$current = calRef.current) == null ? void 0 : _calRef$current.focus();else (_ref$current2 = ref.current) == null ? void 0 : _ref$current2.focus();
  }

  function inRangeValue(value) {
    if (value == null) return value;
    return dates.max(dates.min(value, max), min);
  }

  function formatDate(date) {
    return date instanceof Date && !isNaN(date.getTime()) ? localizer.formatDate(date, currentFormat) : '';
  }

  useImperativeHandle(outerRef, function () {
    return {
      focus: focus
    };
  });
  var shouldRenderList = useFirstFocusedRender(focused, open);
  var inputReadOnly = (inputProps == null ? void 0 : inputProps.readOnly) != null ? inputProps == null ? void 0 : inputProps.readOnly : readOnly;
  return /*#__PURE__*/React.createElement(Widget, _extends({}, elementProps, {
    defaultValue: undefined,
    open: !!open,
    dropUp: dropUp,
    focused: focused,
    disabled: disabled,
    readOnly: readOnly,
    onKeyDown: handleKeyDown,
    onKeyPress: handleKeyPress
  }, focusEvents, {
    className: cn(className, 'rw-date-picker')
  }), /*#__PURE__*/React.createElement(WidgetPicker, {
    className: containerClassName
  }, /*#__PURE__*/React.createElement(DatePickerInput, _extends({}, inputProps, {
    id: inputId,
    ref: ref,
    role: "combobox",
    name: name,
    value: value,
    tabIndex: tabIndex,
    autoFocus: autoFocus,
    placeholder: placeholder,
    disabled: disabled,
    readOnly: inputReadOnly,
    formatter: currentFormat,
    displayFormat: valueDisplayFormat,
    editFormat: valueEditFormat,
    editing: focused,
    localizer: localizer,
    parse: dateParser,
    onChange: handleChange,
    "aria-haspopup": true,
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedby,
    "aria-expanded": !!open,
    "aria-owns": dateId
  })), /*#__PURE__*/React.createElement(InputAddon, {
    icon: selectIcon,
    label: localizer.messages.dateButton(),
    disabled: disabled || readOnly,
    onClick: handleCalendarClick
  })), !!shouldRenderList && /*#__PURE__*/React.createElement(Popup, {
    dropUp: dropUp,
    open: open,
    role: "dialog",
    ref: calRef,
    id: dateId,
    className: "rw-calendar-popup",
    transition: popupTransition,
    onEntering: handleOpening,
    onExited: handleClosing
  }, /*#__PURE__*/React.createElement(Calendar, _extends({
    min: min,
    max: max,
    bordered: false
  }, calendarProps, {
    messages: Object.assign({}, messages, calendarProps == null ? void 0 : calendarProps.messages),
    tabIndex: -1,
    value: value,
    autoFocus: false,
    onChange: handleDateSelect,
    currentDate: currentDate,
    onCurrentDateChange: onCurrentDateChange,
    "aria-hidden": !open,
    "aria-live": "polite",
    "aria-labelledby": inputId
  })), includeTime && /*#__PURE__*/React.createElement(TimeInput, _extends({}, timeInputProps, {
    value: value,
    precision: timePrecision,
    onChange: handleTimeChange,
    datePart: currentDate
  }))));
});
DatePicker.displayName = 'DatePicker';
DatePicker.defaultProps = defaultProps;
export default DatePicker;